import { useEffect } from "react";

/**
 * The GTM attribution token, `k` — captured once on entry, attached to the census submit
 * events, and **completely absent when it is not in the URL**.
 *
 * ## Why it exists
 *
 * A cold outbound wave needs to know *which firm* responded, and that cannot be recovered
 * afterwards: identity here is known only after enrollment (under 35% before, over 98%
 * after), so a visit that never enrols is anonymous forever. The GTM side mints one opaque
 * code per contacted firm and puts it in the link; this reads it back. `cakewalk-gtm`'s
 * `docs/adr/0008` is the written posture that permits it, and `docs/gtm/tracking-conventions.md`
 * is the contract.
 *
 * ## Why a module-level capture rather than reading the URL where it is used
 *
 * The flow is one route per step and `go()` navigates without preserving the query string,
 * so `k` is present only on the **first** render of the first step and is gone by the time
 * anything is submitted. Reading it at the submit site would therefore always find nothing.
 * It is captured on first mount and held for the tab.
 *
 * `sessionStorage` rather than a bare module variable so a mid-flow refresh does not lose it
 * — the flow spans a login and an email round-trip, and a refresh in the middle is ordinary.
 * Session-scoped rather than `localStorage` on purpose: the token describes *this visit from
 * this link*, and one that outlived the tab would attribute a later organic visit to an
 * outbound send nobody clicked.
 *
 * ## What it is not
 *
 * It carries **no company or contact fact** — it is an HMAC digest that means nothing without
 * the cohort file. It is not an identifier of the person filling in the census, it is never
 * written to a census record, and it does not identify anyone to PostHog.
 */

const STORAGE_KEY = "cw_gtm_k";

/**
 * 12 lowercase hex characters, and the validation is not decoration.
 *
 * `k` arrives from a URL, so it is attacker-controlled text. Storing whatever turns up and
 * forwarding it into an analytics property is how a junk value — or something worse-shaped —
 * ends up in an event stream that other systems read. A token that does not match is treated
 * as **absent**, which degrades to exactly the no-token behaviour rather than to an error.
 */
const TOKEN = /^[0-9a-f]{12}$/;

/** Cached for the tab so repeated renders do not re-read storage. `undefined` = not yet read. */
let cached: string | null | undefined;

function readStored(): string | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored !== null && TOKEN.test(stored) ? stored : null;
  } catch {
    // Storage can throw in private modes and locked-down browsers. Attribution is a
    // nice-to-have; the census is not. Failing closed to "no token" is the whole contract.
    return null;
  }
}

/**
 * Capture `k` from the current URL if it is there, and remember it for the tab.
 *
 * Idempotent and safe to call on every render. Returns nothing — `attributionToken()` is the
 * read side, so a component that only submits does not need this hook.
 */
export function useCaptureAttributionToken(): void {
  useEffect(() => {
    if (cached !== undefined && cached !== null) return;
    let fromUrl: string | null = null;
    try {
      fromUrl = new URLSearchParams(window.location.search).get("k");
    } catch {
      fromUrl = null;
    }
    if (fromUrl !== null && TOKEN.test(fromUrl)) {
      cached = fromUrl;
      try {
        window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
      } catch {
        // Held in `cached` for this page load regardless; a refresh loses it, which is the
        // same outcome as never having had it.
      }
      return;
    }
    // Nothing usable in the URL — fall back to what an earlier step stored, if anything.
    cached = readStored();
  }, []);
}

/**
 * The token for this visit, or `null`.
 *
 * `null` is the overwhelmingly common case — every organic visitor, every returning customer,
 * everyone who did not arrive from an outbound link. Callers must treat it as ordinary.
 */
export function attributionToken(): string | null {
  if (cached === undefined) cached = readStored();
  return cached;
}

/**
 * The event properties to spread into a `track` call: `{ gtm_token }` or **nothing at all**.
 *
 * Spreading an empty object is what keeps the no-token path byte-identical to the events
 * this app sent before the token existed. Returning `{ gtm_token: null }` instead would add a
 * property to every census submission in the product's history and change what every existing
 * query over these events sees.
 */
export function attributionProps(): { gtm_token?: string } {
  const token = attributionToken();
  return token === null ? {} : { gtm_token: token };
}

/** Test seam. Not exported from the app's public surface. */
export function __resetAttributionTokenForTests(): void {
  cached = undefined;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to reset if storage is unavailable.
  }
}
